import {
  Alert,
  AlertDescription,
  AlertIcon,
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Code,
  FormControl,
  FormHelperText,
  FormLabel,
  Heading,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Skeleton,
  Stat,
  StatLabel,
  StatNumber,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tooltip,
  Tr,
  VStack,
  useColorModeValue,
  useDisclosure,
} from '@chakra-ui/react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { FiActivity, FiMoreVertical } from 'react-icons/fi'
import { useTranslation } from 'react-i18next'
import { RefreshIconButton } from '@/components/common/RefreshIconButton'
import { useNetworkEnvironment } from '@/hooks/useNetworkEnvironment'
import { useToast } from '@/hooks/useToast'
import {
  monitoringService,
  type SolanaSignerLaneStatus,
  type SolanaSignerRpcEndpointHealth,
  type SolanaSignerRpcHealthState,
} from '@/services/admin/monitoringService'
import {
  resolveSolanaSignerLaneRefetchInterval,
  resolveSolanaSignerRpcHealthRefetchInterval,
} from '@/components/monitoring/solanaSignerLanePolling'

const RECONCILE_REASON_MAX_BYTES = 500

type SolanaSignerLaneMonitorProps = {
  isActive: boolean
}

const STATUS_COLORS: Record<SolanaSignerLaneStatus['status'], string> = {
  idle: 'green',
  preparing: 'blue',
  prepared: 'yellow',
  broadcast: 'purple',
  unknown: 'orange',
  frozen: 'red',
}

const RPC_HEALTH_COLORS: Record<SolanaSignerRpcHealthState, string> = {
  healthy: 'green',
  degraded: 'yellow',
  unavailable: 'red',
  quarantined: 'red',
  stale: 'orange',
  unknown: 'gray',
}

const RPC_ENDPOINT_COLORS: Record<
  SolanaSignerRpcEndpointHealth['status'],
  string
> = {
  healthy: 'green',
  unavailable: 'red',
  quarantined: 'red',
}

function formatTimestamp(value: number | null): string {
  if (value === null || !Number.isFinite(value)) {
    return '--'
  }
  return new Date(value).toLocaleString()
}

function formatTimestampParts(value: number | null): {
  date: string
  time: string
} {
  if (value === null || !Number.isFinite(value)) {
    return { date: '--', time: '--' }
  }
  const timestamp = new Date(value)
  return {
    date: timestamp.toLocaleDateString(),
    time: timestamp.toLocaleTimeString(),
  }
}

function compactIdentifier(value: string | null): string {
  if (!value) {
    return '--'
  }
  if (value.length <= 20) {
    return value
  }
  return `${value.slice(0, 10)}…${value.slice(-8)}`
}

function reasonByteLength(value: string): number {
  return new TextEncoder().encode(value.trim()).byteLength
}

function resolveReconcileErrorDescriptionKey(error: unknown): string {
  const candidate = error as {
    code?: unknown
    response?: {
      data?: {
        code?: unknown
        error?: unknown
      }
    }
  }
  const responseError = candidate?.response?.data?.error
  const code =
    typeof responseError === 'object' &&
    responseError !== null &&
    'code' in responseError
      ? (responseError as { code?: unknown }).code
      : (candidate?.response?.data?.code ?? candidate?.code)
  switch (code) {
    case 'SOLANA_SIGNER_LANE_STALE':
      return 'monitoring.solana.lanes.reconcile.stale'
    case 'SOLANA_SIGNER_LANE_NOT_PROVISIONING':
      return 'monitoring.solana.lanes.reconcile.notProvisioning'
    case 'SOLANA_SIGNER_LANE_REFERENCE_INVALID':
      return 'monitoring.solana.lanes.reconcile.invalidReference'
    case 'AUTHORIZATION_ERROR':
      return 'monitoring.solana.lanes.reconcile.permissionDenied'
    case 'VALIDATION_ERROR':
      return 'monitoring.solana.lanes.reconcile.invalidRequest'
    default:
      return 'monitoring.solana.lanes.reconcile.failedDescription'
  }
}

export function SolanaSignerLaneMonitor({
  isActive,
}: SolanaSignerLaneMonitorProps) {
  const { t } = useTranslation()
  const { currentEnv } = useNetworkEnvironment()
  const toast = useToast()
  const queryClient = useQueryClient()
  const modal = useDisclosure()
  const [selectedLane, setSelectedLane] =
    useState<SolanaSignerLaneStatus | null>(null)
  const [reason, setReason] = useState('')
  const cardBg = useColorModeValue('white', 'gray.700')
  const subtleBg = useColorModeValue('gray.50', 'whiteAlpha.50')
  const mutedText = useColorModeValue('gray.600', 'gray.300')

  const laneQuery = useQuery({
    queryKey: ['monitoring', 'solana-signer-lanes', currentEnv],
    queryFn: () => monitoringService.getSolanaSignerLaneStatus(currentEnv),
    enabled: isActive,
    refetchInterval: resolveSolanaSignerLaneRefetchInterval(isActive),
    refetchIntervalInBackground: false,
    staleTime: 0,
    retry: 1,
  })

  const rpcHealthQuery = useQuery({
    queryKey: ['monitoring', 'solana-signer-rpc-health', currentEnv],
    queryFn: () => monitoringService.getSolanaSignerRpcHealth(currentEnv),
    enabled: isActive,
    refetchInterval: resolveSolanaSignerRpcHealthRefetchInterval(isActive),
    refetchIntervalInBackground: false,
    staleTime: 0,
    retry: 1,
  })

  const reconcileMutation = useMutation({
    mutationFn: async (input: {
      lane: SolanaSignerLaneStatus
      reason: string
    }) => {
      if (!input.lane.intentId) {
        throw new Error(t('monitoring.solana.lanes.reconcile.stale'))
      }
      return monitoringService.reconcileSolanaSignerLane(
        {
          poolVersion: input.lane.poolVersion,
          laneIndex: input.lane.laneIndex,
          laneRevision: input.lane.laneRevision,
          prepareEpoch: input.lane.prepareEpoch,
          intentId: input.lane.intentId,
        },
        input.reason,
        currentEnv
      )
    },
    onSuccess: async result => {
      const toastOptions = {
        titleKey: `monitoring.solana.lanes.reconcile.outcomes.${result.outcome}`,
        descriptionKey: 'monitoring.solana.lanes.reconcile.completedForOrder',
        descriptionParams: {
          orderId: result.orderId,
        },
        duration: 5000,
        isClosable: true,
      }
      if (result.outcome === 'pending') {
        toast.info(toastOptions)
      } else {
        toast.success(toastOptions)
      }
      modal.onClose()
      setSelectedLane(null)
      setReason('')
      await queryClient.invalidateQueries({
        queryKey: ['monitoring', 'solana-signer-lanes', currentEnv],
      })
    },
    onError: error => {
      toast.error({
        titleKey: 'monitoring.solana.lanes.reconcile.failed',
        descriptionKey: resolveReconcileErrorDescriptionKey(error),
        duration: 7000,
        isClosable: true,
      })
    },
  })

  const orderedLanes = useMemo(() => {
    const activeVersion = laneQuery.data?.pool.activeVersion
    return [...(laneQuery.data?.lanes ?? [])].sort((left, right) => {
      const leftActive = left.poolVersion === activeVersion ? 0 : 1
      const rightActive = right.poolVersion === activeVersion ? 0 : 1
      return (
        leftActive - rightActive ||
        left.poolVersion.localeCompare(right.poolVersion) ||
        left.laneIndex - right.laneIndex
      )
    })
  }, [laneQuery.data])

  const reasonBytes = reasonByteLength(reason)
  const reasonValid =
    reason.trim().length > 0 && reasonBytes <= RECONCILE_REASON_MAX_BYTES

  const openReconcile = (lane: SolanaSignerLaneStatus) => {
    setSelectedLane(lane)
    setReason('')
    modal.onOpen()
  }

  const closeReconcile = () => {
    if (reconcileMutation.isPending) {
      return
    }
    modal.onClose()
    setSelectedLane(null)
    setReason('')
  }

  const submitReconcile = () => {
    if (!selectedLane || !reasonValid || reconcileMutation.isPending) {
      return
    }
    reconcileMutation.mutate({ lane: selectedLane, reason: reason.trim() })
  }

  return (
    <>
      <Card bg={cardBg}>
        <CardHeader pb={2}>
          <HStack justify="space-between" align="start" spacing={3}>
            <HStack>
              <FiActivity />
              <Box>
                <Heading size="md">
                  {t('monitoring.solana.lanes.title')}
                </Heading>
                <Text fontSize="sm" color={mutedText} mt={1}>
                  {t('monitoring.solana.lanes.subtitle')}
                </Text>
              </Box>
            </HStack>
            <RefreshIconButton
              label={t('common.refresh')}
              onClick={() => {
                void laneQuery.refetch()
                void rpcHealthQuery.refetch()
              }}
              isLoading={laneQuery.isFetching || rpcHealthQuery.isFetching}
              isDisabled={
                !isActive || laneQuery.isFetching || rpcHealthQuery.isFetching
              }
              flexShrink={0}
            />
          </HStack>
        </CardHeader>
        <CardBody pt={2}>
          {laneQuery.isError && (
            <Alert status="warning" mb={4} borderRadius="md">
              <AlertIcon />
              <AlertDescription fontSize="sm">
                {t('monitoring.solana.lanes.unavailable')}
              </AlertDescription>
            </Alert>
          )}

          {laneQuery.data?.alarm.state === 'due' && (
            <Alert status="error" mb={4} borderRadius="md">
              <AlertIcon />
              <AlertDescription fontSize="sm">
                {t('monitoring.solana.lanes.alarmDue', {
                  time: formatTimestamp(laneQuery.data.alarm.scheduledAtMs),
                })}
              </AlertDescription>
            </Alert>
          )}

          {rpcHealthQuery.isError && (
            <Alert status="warning" mb={4} borderRadius="md">
              <AlertIcon />
              <AlertDescription fontSize="sm">
                {t('monitoring.solana.rpcHealth.unavailable')}
              </AlertDescription>
            </Alert>
          )}

          <Skeleton
            isLoaded={!rpcHealthQuery.isLoading}
            borderRadius="md"
            display={rpcHealthQuery.isError ? 'none' : undefined}
          >
            <Box borderWidth="1px" borderRadius="md" p={4} mb={5}>
              <HStack justify="space-between" align="start" spacing={3} mb={3}>
                <Box>
                  <Text fontWeight="semibold">
                    {t('monitoring.solana.rpcHealth.title')}
                  </Text>
                  <Text fontSize="xs" color={mutedText} mt={1}>
                    {t('monitoring.solana.rpcHealth.observedAt', {
                      time: formatTimestamp(
                        rpcHealthQuery.data?.observedAtMs ?? null
                      ),
                    })}
                  </Text>
                </Box>
                <Badge
                  colorScheme={
                    RPC_HEALTH_COLORS[rpcHealthQuery.data?.state ?? 'unknown']
                  }
                >
                  {t(
                    `monitoring.solana.rpcHealth.states.${rpcHealthQuery.data?.state ?? 'unknown'}`
                  )}
                </Badge>
              </HStack>
              {rpcHealthQuery.data?.endpoints.length ? (
                <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={3}>
                  {rpcHealthQuery.data.endpoints.map(endpoint => (
                    <Box
                      key={endpoint.endpointHash}
                      bg={subtleBg}
                      borderRadius="md"
                      p={3}
                    >
                      <HStack justify="space-between" spacing={2}>
                        <Tooltip label={endpoint.endpointHash} hasArrow>
                          <Code fontSize="xs">
                            {compactIdentifier(endpoint.endpointHash)}
                          </Code>
                        </Tooltip>
                        <Badge
                          colorScheme={RPC_ENDPOINT_COLORS[endpoint.status]}
                        >
                          {t(
                            `monitoring.solana.rpcHealth.endpointStates.${endpoint.status}`
                          )}
                        </Badge>
                      </HStack>
                      <Text fontSize="xs" color={mutedText} mt={2}>
                        {endpoint.lastFailureCode
                          ? t('monitoring.solana.rpcHealth.lastFailure', {
                              code: endpoint.lastFailureCode,
                              time: formatTimestamp(endpoint.lastFailureAtMs),
                            })
                          : t('monitoring.solana.rpcHealth.lastValidated', {
                              time: formatTimestamp(endpoint.lastValidatedAtMs),
                            })}
                      </Text>
                    </Box>
                  ))}
                </SimpleGrid>
              ) : (
                <Text fontSize="sm" color={mutedText}>
                  {t('monitoring.solana.rpcHealth.noEvidence')}
                </Text>
              )}
              <HStack spacing={4} mt={3} flexWrap="wrap">
                <Text fontSize="xs" color={mutedText}>
                  {t('monitoring.solana.rpcHealth.updatedAt', {
                    time: formatTimestamp(
                      rpcHealthQuery.data?.updatedAtMs ?? null
                    ),
                  })}
                </Text>
                <Text fontSize="xs" color={mutedText}>
                  {t('monitoring.solana.rpcHealth.freshUntil', {
                    time: formatTimestamp(
                      rpcHealthQuery.data?.freshUntilMs ?? null
                    ),
                  })}
                </Text>
                <Text fontSize="xs" color={mutedText}>
                  {t('monitoring.solana.rpcHealth.nextProbe', {
                    time: formatTimestamp(
                      rpcHealthQuery.data?.nextProbeAtMs ?? null
                    ),
                  })}
                </Text>
              </HStack>
            </Box>
          </Skeleton>

          <Skeleton isLoaded={!laneQuery.isLoading} borderRadius="md">
            <SimpleGrid columns={{ base: 2, md: 5 }} spacing={4} mb={5}>
              <Stat>
                <StatLabel>
                  {t('monitoring.solana.lanes.summary.activeVersion')}
                </StatLabel>
                <StatNumber fontSize="xl">
                  {laneQuery.data?.pool.activeVersion ?? '--'}
                </StatNumber>
              </Stat>
              <Stat>
                <StatLabel>
                  {t('monitoring.solana.lanes.summary.capacity')}
                </StatLabel>
                <StatNumber fontSize="xl">
                  {laneQuery.data?.pool.activeCapacity ?? '--'}
                </StatNumber>
              </Stat>
              <Stat>
                <StatLabel>
                  {t('monitoring.solana.lanes.summary.busy')}
                </StatLabel>
                <StatNumber fontSize="xl">
                  {laneQuery.data?.pool.activeBusy ?? '--'}
                </StatNumber>
              </Stat>
              <Stat>
                <StatLabel>
                  {t('monitoring.solana.lanes.summary.idle')}
                </StatLabel>
                <StatNumber fontSize="xl">
                  {laneQuery.data?.pool.activeIdle ?? '--'}
                </StatNumber>
              </Stat>
              <Stat>
                <StatLabel>
                  {t('monitoring.solana.lanes.summary.frozen')}
                </StatLabel>
                <StatNumber fontSize="xl">
                  {laneQuery.data?.pool.frozen ?? '--'}
                </StatNumber>
              </Stat>
            </SimpleGrid>

            <HStack spacing={2} mb={3} flexWrap="wrap">
              <Badge colorScheme="purple">
                {laneQuery.data?.chainCode ?? '--'}
              </Badge>
              <Badge
                colorScheme={
                  laneQuery.data?.alarm.state === 'due' ? 'red' : 'gray'
                }
              >
                {t(
                  `monitoring.solana.lanes.alarm.${laneQuery.data?.alarm.state ?? 'unknown'}`
                )}
              </Badge>
              <Text fontSize="xs" color={mutedText}>
                {t('monitoring.solana.lanes.observedAt', {
                  time: formatTimestamp(laneQuery.data?.observedAtMs ?? null),
                })}
              </Text>
            </HStack>

            <TableContainer borderWidth="1px" borderRadius="md">
              <Table size="sm">
                <Thead bg={subtleBg}>
                  <Tr>
                    <Th>{t('monitoring.solana.lanes.columns.pool')}</Th>
                    <Th>{t('monitoring.solana.lanes.columns.lane')}</Th>
                    <Th whiteSpace="normal">
                      {t('monitoring.solana.lanes.columns.status')}
                    </Th>
                    <Th whiteSpace="normal">
                      {t('monitoring.solana.lanes.columns.nonce')}
                    </Th>
                    <Th whiteSpace="normal">
                      {t('monitoring.solana.lanes.columns.intent')}
                    </Th>
                    <Th whiteSpace="normal">
                      {t('monitoring.solana.lanes.columns.operation')}
                    </Th>
                    <Th whiteSpace="normal">
                      {t('monitoring.solana.lanes.columns.reference')}
                    </Th>
                    <Th whiteSpace="normal">
                      {t('monitoring.solana.lanes.columns.evidence')}
                    </Th>
                    <Th>{t('monitoring.solana.lanes.columns.updated')}</Th>
                    <Th textAlign="right">{t('common.actions')}</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {orderedLanes.map(lane => {
                    const isActiveVersion =
                      lane.poolVersion === laneQuery.data?.pool.activeVersion
                    const updatedAt = formatTimestampParts(lane.updatedAtMs)
                    return (
                      <Tr key={`${lane.poolVersion}:${lane.laneIndex}`}>
                        <Td>
                          <VStack align="start" spacing={1}>
                            <Code fontSize="xs">{lane.poolVersion}</Code>
                            <Badge
                              colorScheme={isActiveVersion ? 'green' : 'gray'}
                              fontSize="2xs"
                            >
                              {isActiveVersion
                                ? t('monitoring.solana.lanes.active')
                                : t('monitoring.solana.lanes.recoveryOnly')}
                            </Badge>
                          </VStack>
                        </Td>
                        <Td>
                          <Text>{lane.laneIndex}</Text>
                          <Text
                            fontSize="xs"
                            color={mutedText}
                            whiteSpace="nowrap"
                          >
                            r{lane.laneRevision} / e{lane.prepareEpoch}
                          </Text>
                          {lane.recoveryOwner && (
                            <Text fontSize="xs" color={mutedText}>
                              {lane.recoveryOwner}
                            </Text>
                          )}
                        </Td>
                        <Td whiteSpace="normal" overflowWrap="anywhere">
                          <Badge colorScheme={STATUS_COLORS[lane.status]}>
                            {t(
                              `monitoring.solana.lanes.statuses.${lane.status}`
                            )}
                          </Badge>
                          {lane.errorCode && (
                            <Text fontSize="xs" color="red.500" mt={1}>
                              {lane.errorCode}
                            </Text>
                          )}
                        </Td>
                        <Td whiteSpace="normal" overflowWrap="anywhere">
                          <Tooltip label={lane.nonceAccount} hasArrow>
                            <Code
                              fontSize="xs"
                              whiteSpace="normal"
                              overflowWrap="anywhere"
                            >
                              {compactIdentifier(lane.nonceAccount)}
                            </Code>
                          </Tooltip>
                        </Td>
                        <Td whiteSpace="normal" overflowWrap="anywhere">
                          <Tooltip
                            label={lane.intentId ?? ''}
                            hasArrow
                            isDisabled={!lane.intentId}
                          >
                            <Code
                              fontSize="xs"
                              whiteSpace="normal"
                              overflowWrap="anywhere"
                            >
                              {compactIdentifier(lane.intentId)}
                            </Code>
                          </Tooltip>
                        </Td>
                        <Td whiteSpace="normal" overflowWrap="anywhere">
                          {lane.operation ?? '--'}
                        </Td>
                        <Td whiteSpace="normal" overflowWrap="anywhere">
                          <Tooltip
                            label={lane.businessReference?.id ?? ''}
                            hasArrow
                            isDisabled={!lane.businessReference}
                          >
                            <Code
                              fontSize="xs"
                              whiteSpace="normal"
                              overflowWrap="anywhere"
                            >
                              {compactIdentifier(
                                lane.businessReference?.id ?? null
                              )}
                            </Code>
                          </Tooltip>
                        </Td>
                        <Td whiteSpace="normal" overflowWrap="anywhere">
                          <Text overflowWrap="anywhere">
                            {lane.latestEvidenceStatus ?? '--'}
                          </Text>
                          {lane.signature && (
                            <Tooltip label={lane.signature} hasArrow>
                              <Code
                                fontSize="xs"
                                whiteSpace="normal"
                                overflowWrap="anywhere"
                              >
                                {compactIdentifier(lane.signature)}
                              </Code>
                            </Tooltip>
                          )}
                        </Td>
                        <Td whiteSpace="nowrap">
                          <VStack align="start" spacing={0}>
                            <Text fontSize="xs" lineHeight="shorter">
                              {updatedAt.date}
                            </Text>
                            <Text
                              fontSize="2xs"
                              lineHeight="shorter"
                              color={mutedText}
                            >
                              {updatedAt.time}
                            </Text>
                          </VStack>
                        </Td>
                        <Td textAlign="right">
                          {lane.canReconcile && (
                            <Menu placement="bottom-end" isLazy>
                              <Tooltip label={t('common.actions')} hasArrow>
                                <MenuButton
                                  as={IconButton}
                                  aria-label={t('common.actions')}
                                  icon={<FiMoreVertical />}
                                  size="xs"
                                  variant="ghost"
                                  isDisabled={reconcileMutation.isPending}
                                />
                              </Tooltip>
                              <MenuList minW="180px">
                                <MenuItem
                                  onClick={() => openReconcile(lane)}
                                  isDisabled={reconcileMutation.isPending}
                                >
                                  {t(
                                    'monitoring.solana.lanes.reconcile.button'
                                  )}
                                </MenuItem>
                              </MenuList>
                            </Menu>
                          )}
                        </Td>
                      </Tr>
                    )
                  })}
                  {!laneQuery.isLoading && orderedLanes.length === 0 && (
                    <Tr>
                      <Td
                        colSpan={10}
                        textAlign="center"
                        py={8}
                        color={mutedText}
                      >
                        {t('monitoring.solana.lanes.empty')}
                      </Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>
            </TableContainer>
          </Skeleton>

          <Alert status="info" mt={4} borderRadius="md">
            <AlertIcon />
            <AlertDescription fontSize="sm">
              {t('monitoring.solana.lanes.safetyNotice')}
            </AlertDescription>
          </Alert>
        </CardBody>
      </Card>

      <Modal isOpen={modal.isOpen} onClose={closeReconcile} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {t('monitoring.solana.lanes.reconcile.title')}
          </ModalHeader>
          <ModalCloseButton isDisabled={reconcileMutation.isPending} />
          <ModalBody>
            <VStack align="stretch" spacing={4}>
              <Alert status="warning" borderRadius="md">
                <AlertIcon />
                <AlertDescription fontSize="sm">
                  {t('monitoring.solana.lanes.reconcile.description')}
                </AlertDescription>
              </Alert>
              {selectedLane && (
                <Box bg={subtleBg} p={3} borderRadius="md">
                  <Text fontSize="sm">
                    {t('monitoring.solana.lanes.reconcile.target', {
                      pool: selectedLane.poolVersion,
                      lane: selectedLane.laneIndex,
                    })}
                  </Text>
                  <Code mt={2} fontSize="xs" overflowWrap="anywhere">
                    {selectedLane.intentId}
                  </Code>
                </Box>
              )}
              <FormControl
                isRequired
                isInvalid={reasonBytes > RECONCILE_REASON_MAX_BYTES}
              >
                <FormLabel>
                  {t('monitoring.solana.lanes.reconcile.reason')}
                </FormLabel>
                <Textarea
                  value={reason}
                  onChange={event => setReason(event.target.value)}
                  placeholder={t(
                    'monitoring.solana.lanes.reconcile.reasonPlaceholder'
                  )}
                  isDisabled={reconcileMutation.isPending}
                  rows={4}
                  autoFocus
                />
                <FormHelperText>
                  {t('monitoring.solana.lanes.reconcile.reasonBytes', {
                    count: reasonBytes,
                    max: RECONCILE_REASON_MAX_BYTES,
                  })}
                </FormHelperText>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter gap={3}>
            <Button
              onClick={closeReconcile}
              isDisabled={reconcileMutation.isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button
              colorScheme="orange"
              onClick={submitReconcile}
              isLoading={reconcileMutation.isPending}
              isDisabled={!reasonValid || !selectedLane}
            >
              {t('monitoring.solana.lanes.reconcile.confirm')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  )
}

export default SolanaSignerLaneMonitor
